import type { FeatureTranslation } from "../types";

export const akademikEn: Record<string, FeatureTranslation> = {
  "presensi-harian": {
    label: "Daily Attendance",
    navDescription: "Card taps, lateness, summaries",
    summary:
      "Arrival and departure recorded by card tap, with hours that can differ by day and by role.",
    detail: {
      title: "Attendance that is done *the moment a card is tapped*",
      lead: "Tap at the gate, summaries build themselves, and parents know without having to ask",
      intro:
        "No more manual registers reconciled again at the end of every month. Students and teachers tap their card on arrival and departure, the system matches it against the school's attendance schedule, then marks who was on time and who was late.",
      highlights: [
        "Arrival and departure times can differ for each day of the week",
        "Separate rules for students and for teachers and staff",
        "A maximum lateness threshold sets the status automatically",
      ],
      benefits: [
        {
          title: "Rules that follow your school's habits",
          body: "Set arrival, departure, and lateness thresholds per day. Monday can differ from Friday, and staff rules can differ from student rules.",
        },
        {
          title: "Lateness detected on its own",
          body: "The system compares the tap time against the maximum lateness threshold, then separates out the late list without anyone counting by hand.",
        },
        {
          title: "Student and staff summaries stay apart",
          body: "Student summaries and teacher and staff summaries stand on their own, so form teachers and HR each see the data relevant to them.",
        },
        {
          title: "A way out for edge cases",
          body: "A forgotten card or a faulty reader can still be handled through manual attendance, recorded as an added entry with its own trail of changes.",
        },
      ],
      screensTitle: "From settings to summary",
      screensLead:
        "Set it once at the start of the academic year, and what remains is simply reading the summary.",
      screens: [
        {
          alt: "Daily attendance time settings per day for students and staff",
          caption: "Attendance Time Settings",
          note: "Arrival, departure, and lateness threshold per day",
        },
        {
          alt: "Daily student attendance summary in the Cards School dashboard",
          caption: "Student Attendance Summary",
          note: "Per-student summary with search and filters",
        },
        {
          alt: "A child's daily attendance in the parent app",
          caption: "Attendance in the parent app",
          note: "Parents see their child's attendance day by day",
        },
      ],
      faq: [
        {
          q: "What if arrival times differ from day to day?",
          a: "The attendance schedule is set per day, Monday through Sunday. You can define different start times, end times, and maximum lateness thresholds for each one.",
        },
        {
          q: "Do teachers and students follow the same rules?",
          a: "They do not have to. There is an option to separate student attendance times from teacher and staff times, each with its own schedule.",
        },
        {
          q: "What if a student forgets their card?",
          a: "Staff can add attendance manually from the dashboard. The entry is recorded as a manual one, so it remains visible in the summary and can be audited.",
        },
      ],
      metaTitle: "School Attendance Software with RFID Cards",
      metaDescription:
        "Record arrival and departure by RFID card tap. Set hours and lateness thresholds per day, separate student and staff rules, summaries build themselves.",
      keywords: [
        "school attendance software",
        "rfid student attendance",
        "online school attendance system",
        "daily student attendance",
        "teacher attendance app",
      ],
    },
  },

  "presensi-mapel": {
    label: "Lesson Attendance",
    navDescription: "Per-lesson entry & summaries",
    summary:
      "Teachers record attendance per subject, and summaries build up per student.",
    detail: {
      title: "Attendance per lesson, *not only per day*",
      lead: "Teachers record attendance against their own timetable, alongside a note on what was taught",
      intro:
        "Being present at the gate does not mean being present in class. Lesson attendance closes that gap: teachers record attendance for their own periods, can select several students at once, and note the material covered that day at the same time.",
      highlights: [
        "Attendance entry follows the teacher's timetable",
        "Select several students at once rather than one by one",
        "Lesson notes and QR attendance in the same flow",
      ],
      benefits: [
        {
          title: "Tied to the teaching timetable",
          body: "Teachers can only record attendance for their own scheduled periods. When no timetable is found, the system says so rather than accepting the wrong data.",
        },
        {
          title: "Entry that is not a chore",
          body: "Several students can be selected and marked together, so filling in a class register does not eat into the lesson.",
        },
        {
          title: "The material is captured too",
          body: "Lesson notes are filled in alongside attendance. Teachers can pick from existing material or add something new.",
        },
        {
          title: "A history you can trace",
          body: "Lesson attendance history can be searched and filtered, making it easy for a form teacher to spot a pattern of absence in a particular subject.",
        },
      ],
      screensTitle: "A teacher's flow on one screen",
      screensLead:
        "From choosing the period, to marking attendance, to writing up the material — one continuous sequence.",
      screens: [
        {
          alt: "Lesson attendance entry page in the Cards School dashboard",
          caption: "Attendance Entry",
          note: "Follows the teacher's own timetable",
        },
        {
          alt: "Marking several students present at once",
          caption: "Marking Attendance",
          note: "Select several students and mark them together",
        },
        {
          alt: "Lesson notes with material selection",
          caption: "Lesson Notes",
          note: "Pick existing material or add something new",
        },
      ],
      faq: [
        {
          q: "Can a teacher record attendance outside their teaching hours?",
          a: "No. Attendance entry is tied to the teaching timetable, and the system declines when no schedule exists for that period.",
        },
        {
          q: "How does lesson attendance relate to daily attendance?",
          a: "They stand apart. Daily attendance records presence at school through a card tap, while lesson attendance records presence in each period.",
        },
        {
          q: "Is QR-based attendance available?",
          a: "Yes, QR attendance is one of the ways presence can be taken for a lesson, alongside manual entry by the teacher.",
        },
      ],
      metaTitle: "Lesson Attendance & Teaching Notes Software",
      metaDescription:
        "Teachers record attendance per period against their timetable, select several students at once, and note the material taught in the same flow.",
      keywords: [
        "lesson attendance software",
        "per period attendance",
        "teacher lesson log",
        "digital teaching notes",
        "class attendance app",
      ],
    },
  },

  "presensi-kegiatan": {
    label: "Activity Attendance",
    navDescription: "Prayers, clubs, school activities",
    summary:
      "Attendance for activities outside lesson time, with summaries by activity or by student.",
    detail: {
      title: "Attendance at activities *gets its own record too*",
      lead: "Congregational prayers, clubs, and other school activities with summaries that read both ways",
      intro:
        "What happens outside lesson time is often the hardest to capture, even though that is exactly where pastoral development takes place. Activity attendance gives each event its own register, with summaries that can be read by activity or by student.",
      highlights: [
        "One activity, one register and its own settings",
        "Summaries by activity and by student",
        "Applies to students as well as teachers and staff",
      ],
      benefits: [
        {
          title: "Each activity configured on its own",
          body: "Congregational prayers, clubs, or annual events each carry their own attendance settings, rather than being forced onto one shared schedule.",
        },
        {
          title: "Two ways to read the data",
          body: "The by-activity summary answers who attended an event; the by-student summary answers how active a particular child has been.",
        },
        {
          title: "Supervisors are counted too",
          body: "Activity attendance also covers teachers and staff, so a supervisor's presence can be traced just as a participant's can.",
        },
        {
          title: "Real evidence for pastoral work",
          body: "Activity attendance data gives an objective basis for conduct assessment and pastoral development, rather than a supervisor's impression.",
        },
      ],
      screensTitle: "A child's activities in the parent app",
      screensLead:
        "Parents can see for themselves which activities their child joins and how regularly they attend.",
      screens: [
        {
          alt: "Student activity attendance in the CARDS parent app",
          caption: "Activity Attendance",
          note: "A child's attendance record for each activity",
        },
      ],
      faq: [
        {
          q: "Which activities can be recorded?",
          a: "Activities are defined by the school — congregational prayers, Qur'an study, clubs, assemblies, or anything else. Each carries its own attendance settings.",
        },
        {
          q: "Does activity attendance use the same card?",
          a: "Yes. The card used for daily attendance is also used for activity attendance, so no additional identity device is needed.",
        },
        {
          q: "Can activity summaries be pulled per student?",
          a: "Yes. Summaries are available by activity and by student, so the same data can be read from two angles as needed.",
        },
      ],
      metaTitle: "School Activity & Extracurricular Attendance",
      metaDescription:
        "Record attendance for congregational prayers, clubs, and other school activities with a student card. Summaries available by activity or by student.",
      keywords: [
        "school activity attendance",
        "extracurricular attendance software",
        "club attendance tracking",
        "student activity records",
        "prayer attendance system",
      ],
    },
  },

  rapor: {
    label: "Digital Report Cards",
    navDescription: "Academic marks & conduct",
    summary:
      "Report cards produced digitally and opened by parents straight from the app.",
    detail: {
      title: "A report card you can open *any time, not once a year*",
      lead: "Academic marks and conduct assessment produced digitally and delivered straight to parents",
      intro:
        "A paper report card changes hands twice a year, then sits in a drawer. A digital one makes a child's progress available whenever a parent wants it, with no waiting for handover day and no worrying about a lost sheet.",
      highlights: [
        "Academic marks and conduct assessment in one report",
        "Opened by parents directly in the CARDS app",
        "Built on attendance data already captured",
      ],
      benefits: [
        {
          title: "No waiting for handover day",
          body: "Parents open the report card from the app whenever they like. A child's progress becomes an ongoing conversation rather than a surprise at the end of term.",
        },
        {
          title: "Marks and conduct side by side",
          body: "The report carries academic marks alongside conduct assessment, so the picture of a child does not stop at subject scores.",
        },
        {
          title: "Built on data you already have",
          body: "Attendance and pastoral records already live in the same system, so preparing a report does not start with gathering scattered data.",
        },
        {
          title: "Records that do not go missing",
          body: "Report cards are stored in the system, so there are no more requests for a reprint because a sheet was mislaid or damaged.",
        },
      ],
      screensTitle: "The report card in a parent's hand",
      screensLead:
        "Opened from the Academic menu, alongside attendance and the timetable.",
      screens: [
        {
          alt: "Student report card in the CARDS parent app",
          caption: "Student Report Card",
          note: "Academic marks and conduct assessment",
        },
      ],
      faq: [
        {
          q: "Can the report format follow our curriculum?",
          a: "Report structure is configured during implementation to match the assessment components used at your school. Our team will work through it with your curriculum lead.",
        },
        {
          q: "Who can see a particular student's report card?",
          a: "The report is visible to that student's parents through their app account, and to the school roles you grant permission to in the user settings.",
        },
        {
          q: "Does a digital report card replace the printed one?",
          a: "The digital report makes marks available at any time. Printing for administrative purposes can still be accommodated according to school policy.",
        },
      ],
      metaTitle: "Digital Student Report Cards for Parents",
      metaDescription:
        "Academic marks and conduct assessment produced digitally and opened by parents whenever they like, built on the school's own attendance data.",
      keywords: [
        "digital report card",
        "online student report card",
        "e-report school software",
        "student grades online",
        "student conduct report",
      ],
    },
  },

  "tugas-jadwal": {
    label: "Assignments & Timetable",
    navDescription: "Teacher assignments & digital timetable",
    summary:
      "Teachers set assignments while students and parents follow a timetable that stays current.",
    detail: {
      title: "Assignments that *do not stop at the whiteboard*",
      lead: "Teachers set work with deadlines and attachments, then follow it through to submission and marking",
      intro:
        "Work announced out loud is easily forgotten, and parents often have no idea a deadline is approaching. Through the CARDS Edu app, teachers create an assignment with its date and time, attach the material, then track who has submitted and mark the results.",
      highlights: [
        "Assignments with a date and time deadline, plus attachments",
        "Per-student status: assigned, submitted, marked, closed",
        "Enter marks and filter results from the teacher app",
      ],
      benefits: [
        {
          title: "A deadline that is clear from the outset",
          body: "Teachers set the closing date and time when creating the assignment, so there is no argument about when submissions were due.",
        },
        {
          title: "The material comes attached",
          body: "Supporting files are attached to the assignment and can be removed or replaced, so students do not have to hunt for the source themselves.",
        },
        {
          title: "See who is falling behind",
          body: "Per-student status separates those newly assigned, those who have submitted, those already marked, and those whose deadline has passed.",
        },
        {
          title: "Marking in the same app",
          body: "Marks are entered directly from the teacher app, and results can be filtered — including seeing the highest marks in a class.",
        },
      ],
      screensTitle: "From setting work to marking it",
      screensLead:
        "Three screens teachers move through in order, all from the CARDS Edu app.",
      screens: [
        {
          alt: "Creating a new assignment with date selection in the teacher app",
          caption: "New Assignment",
          note: "Set the class, deadline, and attachments",
        },
        {
          alt: "Assignment detail with the list of assigned students",
          caption: "Assignment Detail",
          note: "Each student's status for that assignment",
        },
        {
          alt: "Entering marks for a student assignment in the teacher app",
          caption: "Enter Marks",
          note: "Mark the work and filter the class results",
        },
      ],
      faq: [
        {
          q: "Can parents see their child's assignments?",
          a: "The timetable and academic information for a child are available in the parent app, so parents can follow their learning without having to ask a teacher.",
        },
        {
          q: "Can an assignment be given to more than one class?",
          a: "When creating an assignment, teachers choose the target class through a class search, so work can be directed to whichever groups they teach.",
        },
        {
          q: "What happens after the deadline passes?",
          a: "The assignment moves to a closed status, and the student list separates those who submitted from those who did not, so teachers know who needs following up.",
        },
      ],
      metaTitle: "Student Assignments & Digital Class Timetable",
      metaDescription:
        "Teachers create assignments with deadlines and attachments, track submissions per student, then mark from the app. Timetables stay open to parents.",
      keywords: [
        "student assignment software",
        "digital class timetable",
        "online homework assignment",
        "teacher grading app",
        "cards edu teacher",
      ],
    },
  },

  "kalender-akademik": {
    label: "Academic Calendar",
    navDescription: "School dates through the year",
    summary:
      "An academic calendar teachers, students, and parents all read from one source.",
    detail: {
      title: "One calendar *everybody reads*",
      lead: "School events, holidays, and key dates in an academic calendar that is open to all",
      intro:
        "An academic calendar pinned up in the staff room never reaches parents. Once it lives in the app, everyone reads the same source — exam dates, holidays, and school events stop travelling by forwarded message.",
      highlights: [
        "School events laid out month by month across the year",
        "Opened by teachers, students, and parents in the same app",
        "Replaces chain messages that are easily lost",
      ],
      benefits: [
        {
          title: "No two versions of the schedule",
          body: "Because everyone reads the same calendar, there is no longer a gap between what parents were told and what the school is actually doing.",
        },
        {
          title: "Families can plan ahead",
          body: "Holidays and key events are visible well in advance, so families can arrange their own commitments without last-minute surprises.",
        },
        {
          title: "Always there in the app",
          body: "The calendar sits in the Academic menu with attendance and report cards, so there is no leaflet to find or message thread to scroll.",
        },
        {
          title: "Changes reach everyone",
          body: "When a date shifts, the change is made in one place and is immediately read by everyone who opens it.",
        },
      ],
      screensTitle: "The academic calendar month by month",
      screensLead:
        "Shown monthly so the nearest events read immediately on opening.",
      screens: [
        {
          alt: "Monthly academic calendar in the CARDS app",
          caption: "Academic Calendar",
          note: "Events and key dates for each month",
        },
      ],
      faq: [
        {
          q: "Who fills in the academic calendar?",
          a: "The calendar is managed from the school dashboard by the roles you grant permission to, then appears in the teacher, student, and parent apps.",
        },
        {
          q: "Can the calendar hold events for specific year groups?",
          a: "Events are structured to suit your school's needs. The details are worked through during implementation with your curriculum lead.",
        },
        {
          q: "Do parents get notified about events?",
          a: "For anything that needs to be actively communicated, the school can use the Announcements module, which reaches the app and parents' WhatsApp.",
        },
      ],
      metaTitle: "Online School Academic Calendar",
      metaDescription:
        "An academic calendar teachers, students, and parents read from one source. Events, holidays, and school activities laid out month by month.",
      keywords: [
        "school academic calendar",
        "digital school calendar",
        "online school events calendar",
        "school year planner",
        "school calendar app",
      ],
    },
  },
  "presensi-wajah": {
    label: "Face Attendance",
    navDescription: "Face recognition & attendance devices",
    summary:
      "Attendance by face recognition on on-site devices — complementing card taps, with per-device logs.",
    detail: {
      title: "Present by simply *facing the device*",
      lead: "Face recognition complements card taps: face data syncs to each device, and every check-in leaves a log",
      intro:
        "A card can be left at home; a face cannot. Face-recognition devices at the gate or kiosk identify arriving students and staff, record their attendance, and keep a log per device — running side by side with the card-tap attendance already in place.",
      highlights: [
        "Face recognition on the school's attendance devices",
        "Face data synced from the system to every device",
        "Traceable attendance logs per device",
      ],
      benefits: [
        {
          title: "Not dependent on a card in a pocket",
          body: "A student whose card was left behind is still recorded present — a face is the one identity that always comes along.",
        },
        {
          title: "Devices managed from the dashboard",
          body: "Attendance devices are registered in the system, and face data syncs to them from there — no configuring each unit by hand.",
        },
        {
          title: "Every recognition leaves a log",
          body: "The face-device attendance log keeps who was recognised, on which device, and when — so anomalies are easy to trace.",
        },
        {
          title: "One summary with card attendance",
          body: "Face attendance results feed the same daily attendance summary, so form teachers read one report, not two.",
        },
      ],
      screensTitle: "From face enrolment to the summary",
      screensLead:
        "A flow that runs once at the start, then works by itself every morning.",
      steps: [
        {
          title: "Register the devices",
          body: "Face attendance devices are registered under attendance device data in the school dashboard.",
        },
        {
          title: "Sync the face data",
          body: "Members' face data syncs from the system to the devices through the face sync menu.",
        },
        {
          title: "Students face the device",
          body: "On arrival, a student or staff member simply faces the device; attendance is recorded on the spot.",
        },
        {
          title: "Read the logs and summary",
          body: "Per-device logs can be traced, and the results merge into the school's daily attendance summary.",
        },
      ],
      faq: [
        {
          q: "Does face attendance replace card taps?",
          a: "No — the two run side by side. A school can use cards, faces, or both, depending on the devices available at each point.",
        },
        {
          q: "How does face data reach the devices?",
          a: "Through synchronisation from the dashboard: members' face data is pushed to registered devices, so new students do not have to be enrolled manually at each unit.",
        },
        {
          q: "Can the logs be audited?",
          a: "Yes. The face-device attendance log records every recognition per device and can be traced from the dashboard.",
        },
      ],
      metaTitle: "Face Recognition Attendance for Schools",
      metaDescription:
        "Face attendance for students, teachers, and staff: face data syncs to devices, per-device logs are kept, and results merge into the daily summary.",
      keywords: [
        "face recognition school attendance",
        "student face attendance",
        "school face recognition device",
        "biometric attendance school",
        "attendance devices for schools",
      ],
    },
  },

  "kuis-ai": {
    label: "AI Quiz",
    navDescription: "Generate questions automatically with AI",
    summary:
      "Generate quiz and flashcard questions automatically with AI from the teacher's own material, with scheduling and result summaries.",
    detail: {
      title: "Quiz questions *drafted by AI* from the teacher's own material",
      lead: "Upload material or pull from the teacher's journal, set the count and weighting, and AI drafts the questions",
      intro:
        "Writing questions is hours of work that repeats every week. AI Quiz cuts it down: the teacher supplies the material — via file upload, the teacher's journal, or typed text — and AI generates quiz or flashcard questions that can still be edited before being saved to the question bank, scheduled, and summarised.",
      highlights: [
        "Automatic question generation: choose quiz or flashcard",
        "Source material from file uploads, teacher journals, or typed text",
        "Question bank, quiz schedules, a participant page, and result summaries",
      ],
      benefits: [
        {
          title: "Hours of question-writing become minutes",
          body: "Set the configuration — question type, count, and weighting — and AI drafts them from the material you provide. The time saved goes back to teaching.",
        },
        {
          title: "The questions remain the teacher's",
          body: "Generated questions can be edited one by one or regenerated before saving. AI prepares the draft; the teacher makes the call.",
        },
        {
          title: "Made once, kept in the question bank",
          body: "Finished questions join the quiz collection and can be reused across classes and academic years.",
        },
        {
          title: "From schedule to marked results",
          body: "Quizzes are scheduled, students take them through the participant page, and results are summarised automatically — one full loop with no paper answer sheets.",
        },
      ],
      screensTitle: "From configuration to the quiz collection",
      screensLead:
        "The teacher configures the quiz, AI drafts the questions, students take it from any device, and every question set lands in the quiz collection.",
      screens: [
        {
          alt: "Create Quiz form with quiz or flashcard question types in the Cards School dashboard",
          caption: "Quiz Configuration",
          note: "Choose quiz or flashcard, along with the question types, count, and weighting",
        },
        {
          alt: "Quiz-taking page with question navigation, remaining time, and a submit button",
          caption: "Quiz-taking Page",
          note: "Students work through numbered questions against the clock",
        },
        {
          alt: "Quiz collection table of AI-generated question sets with filters, search, and Excel export",
          caption: "Quiz Collection",
          note: "Generated question sets stay organised, ready for reuse or export",
        },
        {
          alt: "Quiz-taking view on a phone screen",
          caption: "Taking quizzes on a phone",
          note: "The same quiz works comfortably on the student's own phone",
        },
        {
          alt: "Score and correct-answer recap shown after submitting a quiz, on a phone screen",
          caption: "Score & answer recap",
          note: "The moment they submit, students see their score along with the correct answers",
        },
        {
          alt: "Student quiz leaderboard with a top-three podium, on a phone screen",
          caption: "Leaderboard",
          note: "Rankings between students turn every quiz into healthy competition",
        },
      ],
      faq: [
        {
          q: "Where does the AI take its material from?",
          a: "From what the teacher provides — an uploaded file, the teacher's journal in the system, or typed text. The AI drafts questions from that reference, not from outside it.",
        },
        {
          q: "What if a generated question is off the mark?",
          a: "Every generated question can be edited individually, or regenerated, before it is saved to the question bank.",
        },
        {
          q: "How do students take the quiz?",
          a: "A scheduled quiz is taken through the participant page, and the results flow into the quiz summary for the teacher.",
        },
      ],
      metaTitle: "AI Quiz — Automatic Question Generation for Schools",
      metaDescription:
        "Generate quiz and flashcard questions automatically with AI from the teacher's material — edit, save to the question bank, schedule, and summarise results.",
      keywords: [
        "ai question generator",
        "school quiz app",
        "digital question bank",
        "ai for teachers",
        "automatic quiz generation",
      ],
    },
  },
};
